import React, { useCallback, useEffect, useState, useRef, Fragment, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Crown, Building2, Users, Star, Receipt, Info, FileText, HandCoins, Globe, GraduationCap, Landmark, Sparkles, Play, Pause, SkipForward } from 'lucide-react';
import { SlideAtmosphere } from '../components/deck/SlideAtmosphere';
import { PunjabiPlacard } from '../components/deck/PunjabiPlacard';
import { DelayedVideo } from '../components/deck/DelayedVideo';
const FlippableCard = ({
  card,
  isActive,
  onClick
}: {
  card: any;
  isActive?: boolean;
  onClick?: () => void;
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  // Reset flip state when card becomes inactive
  useEffect(() => {
    if (isActive === false) {
      setIsFlipped(false);
    }
  }, [isActive]);
  return <div className="aspect-[3/4] relative cursor-pointer group" style={{
    perspective: '1000px'
  }} onClick={() => {
    if (onClick) onClick();
    if (isActive !== false) setIsFlipped(!isFlipped);
  }} data-id="element-7044">
      <motion.div className="w-full h-full relative" animate={{
      rotateY: isFlipped ? 180 : 0
    }} transition={{
      duration: 0.8,
      type: 'spring',
      stiffness: 200,
      damping: 25
    }} style={{
      transformStyle: 'preserve-3d'
    }} data-id="element-7045">
        {/* Front */}
        <div className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden shadow-[0_0_30px_rgba(212,175,55,0.15)] bg-black" style={{
        backfaceVisibility: 'hidden'
      }} data-id="element-7046">
          <img src={card.src} alt={card.name} className="w-full h-full object-cover" data-id="element-7047" />
        </div>

        {/* Back */}
        <div className="absolute inset-0 bg-[#0a0604] border border-[#D4AF37]/40 flex flex-col items-center justify-center p-5 text-center shadow-[inset_0_0_40px_rgba(0,0,0,0.8)]" style={{
        transform: 'rotateY(180deg)',
        backfaceVisibility: 'hidden'
      }} data-id="element-7048">
          <div className="absolute inset-0 opacity-10 bg-[url('https://cdn.magicpatterns.com/uploads/3hBK2oMvmeZZy5P54xmeKQ/Lahore_Court_Collection.png')] bg-cover bg-center mix-blend-overlay" data-id="element-7049" />
          <div className="relative z-10 w-full" data-id="element-7050">
            <Crown className="w-6 h-6 text-[#D4AF37] mx-auto mb-4 opacity-80" data-id="element-7051" />
            <h4 className="text-[#FDFBF7] font-serif text-lg mb-3" data-id="element-7052">
              {card.name}
            </h4>
            <div className="w-12 h-px bg-[#D4AF37]/50 mx-auto mb-4" data-id="element-7053" />
            <p className="text-white/70 text-xs font-light leading-relaxed mb-6" data-id="element-7054">
              {card.desc}
            </p>
            {card.stats && <div className="grid grid-cols-2 gap-3 text-left bg-black/40 p-3 border border-[#D4AF37]/20 backdrop-blur-sm" data-id="element-7055">
                {card.stats.map((stat: any, idx: number) => <div key={idx} data-id="element-7056">
                    <p className="text-[#D4AF37] text-[9px] uppercase tracking-widest mb-0.5" data-id="element-7057">
                      {stat.label}
                    </p>
                    <p className="text-white/90 text-xs font-light" data-id="element-7058">
                      {stat.value}
                    </p>
                  </div>)}
              </div>}
          </div>
        </div>
      </motion.div>
    </div>;
};
const TreasuryFan = ({
  cards
}: {
  cards: any[];
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  return <div className="relative w-full max-w-4xl mx-auto h-[320px] flex justify-center items-end" data-id="element-7059">
      {/* Backdrop to dismiss active card */}
      <AnimatePresence data-id="element-7060">
        {activeIndex !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm cursor-pointer" onClick={() => setActiveIndex(null)} data-id="element-7061" />}
      </AnimatePresence>

      {cards.map((card, i) => {
      const isActive = activeIndex === i;
      const offset = i - Math.floor(cards.length / 2);
      const rotation = isActive ? 0 : offset * 12;
      const yOffset = isActive ? -120 : Math.abs(offset) * 15;
      const xOffset = isActive ? 0 : offset * 80;
      const zIndex = isActive ? 50 : 10 + i;
      return <motion.div key={i} className="absolute bottom-0 w-48 sm:w-56" style={{
        zIndex
      }} animate={{
        rotate: rotation,
        y: yOffset,
        scale: isActive ? 1.4 : 1,
        x: xOffset
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 25
      }} data-id="element-7062">
            <FlippableCard card={card} isActive={isActive} onClick={() => {
          if (!isActive) {
            setActiveIndex(i);
          }
        }} data-id="element-7063" />
          </motion.div>;
    })}
    </div>;
};
const DailyPacketAnimation = () => {
  const [packetState, setPacketState] = useState<'unopened' | 'opening' | 'revealed'>('unopened');
  return <div className="flex flex-col items-center justify-center w-full h-[560px] relative" data-id="element-7064">
      <AnimatePresence mode="wait" data-id="element-7065">
        {packetState === 'unopened' && <motion.div key="unopened" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 1.1
      }} className="flex flex-col items-center text-center max-w-md mx-auto relative" data-id="element-7066">
            <motion.div className="relative w-64 sm:w-72 mb-8" data-id="element-7067">
              <motion.div animate={{
            scale: [1, 1.05, 1],
            opacity: [0.5, 0.8, 0.5]
          }} transition={{
            duration: 3,
            repeat: Infinity
          }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.35),_transparent_65%)] blur-2xl pointer-events-none" data-id="element-7068" />
              <motion.button type="button" onClick={() => {
            setPacketState('opening');
            setTimeout(() => setPacketState('revealed'), 1900);
          }} whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.96
          }} className="relative block w-full cursor-pointer" data-id="element-7069">
                <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Sealed envelope" className="w-full drop-shadow-[0_25px_50px_rgba(0,0,0,0.7)]" data-id="element-7070" />
                <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase whitespace-nowrap" data-id="element-7071">
                  Click to open
                </span>
              </motion.button>
            </motion.div>
          </motion.div>}

        {packetState === 'opening' && <motion.div key="opening" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative flex items-center justify-center w-full h-full" data-id="element-7072">
            <motion.div initial={{
          scale: 0.4,
          opacity: 0
        }} animate={{
          scale: 4,
          opacity: [0, 0.9, 0]
        }} transition={{
          duration: 1.8,
          ease: 'easeOut'
        }} className="absolute w-96 h-96 rounded-full bg-[radial-gradient(circle,_rgba(212,175,55,0.6),_transparent_60%)] blur-3xl" data-id="element-7073" />
            <motion.div initial={{
          scale: 1,
          opacity: 1
        }} animate={{
          scale: [1, 1.1, 0.6],
          opacity: [1, 1, 0],
          rotateZ: [0, -3, 4, -2, 0]
        }} transition={{
          duration: 1.8,
          times: [0, 0.5, 1],
          ease: 'easeInOut'
        }} className="relative w-64 sm:w-72 z-10" data-id="element-7074">
              <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Opening envelope" className="w-full drop-shadow-[0_0_80px_rgba(212,175,55,0.7)]" data-id="element-7075" />
            </motion.div>
            {Array.from({
          length: 20
        }).map((_, i) => {
          const angle = i / 20 * Math.PI * 2;
          const distance = 280;
          return <motion.div key={i} initial={{
            x: 0,
            y: 0,
            opacity: 0,
            scale: 0
          }} animate={{
            x: Math.cos(angle) * distance,
            y: Math.sin(angle) * distance,
            opacity: [0, 1, 0],
            scale: [0, 1.2, 0]
          }} transition={{
            duration: 1.6,
            delay: 0.3 + i % 4 * 0.05,
            ease: 'easeOut'
          }} className="absolute w-2 h-2 bg-[#D4AF37] rounded-full shadow-[0_0_14px_rgba(212,175,55,1)]" data-id="element-7076" />;
        })}
            <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: [0, 1, 1, 0],
          y: 0
        }} transition={{
          duration: 1.8,
          times: [0, 0.3, 0.7, 1]
        }} className="absolute bottom-16 font-serif text-xl text-[#D4AF37] tracking-[0.35em] uppercase" data-id="element-7077">
              The seal breaks…
            </motion.p>
          </motion.div>}

        {packetState === 'revealed' && <motion.div key="revealed" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex flex-col items-center justify-center" data-id="element-7078">
            <div className="flex justify-center mb-8" data-id="element-7079">
              <motion.div initial={{
            y: 40,
            opacity: 0,
            rotate: 0
          }} animate={{
            y: 0,
            opacity: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            delay: 0.1
          }} className="w-48 aspect-[3/4]" data-id="element-7080">
                <img src="https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png" className="w-full h-full object-cover shadow-2xl" alt="Card" data-id="element-7081" />
              </motion.div>
            </div>
            <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1
        }} onClick={() => setPacketState('unopened')} className="px-6 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37]/10 transition-colors" data-id="element-7082">
              Reset Animation
            </motion.button>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export const SponsorDeckPage = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [archiveIndex, setArchiveIndex] = useState(0);
  const [drinkIndex, setDrinkIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [slide7AudioPlaying, setSlide7AudioPlaying] = useState(false);
  const slide7AudioRef = useRef<HTMLAudioElement>(null);
  const slide7VideoRef = useRef<HTMLVideoElement>(null);
  const slide7TimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [slide7bAudioPlaying, setSlide7bAudioPlaying] = useState(false);
  const slide7bAudioRef = useRef<HTMLAudioElement>(null);
  const slide7bVideoRef = useRef<HTMLVideoElement>(null);
  const slide7bTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSlide7Play = () => {
    if (slide7AudioPlaying) {
      // Pause
      if (slide7AudioRef.current) slide7AudioRef.current.pause();
      if (slide7VideoRef.current) slide7VideoRef.current.pause();
      if (slide7TimeoutRef.current) clearTimeout(slide7TimeoutRef.current);
      setSlide7AudioPlaying(false);
    } else {
      // Play
      if (isPlaying) {
        if (audioRef.current) audioRef.current.pause();
        setIsPlaying(false);
      }
      if (slide7AudioRef.current) {
        slide7AudioRef.current.play();
        setSlide7AudioPlaying(true);
        if (slide7VideoRef.current) {
          if (slide7VideoRef.current.currentTime === 0) {
            slide7TimeoutRef.current = setTimeout(() => {
              if (slide7VideoRef.current) {
                slide7VideoRef.current.playbackRate = 0.5;
                slide7VideoRef.current.play();
              }
            }, 10000);
          } else {
            slide7VideoRef.current.playbackRate = 0.5;
            slide7VideoRef.current.play();
          }
        }
      }
    }
  };
  const handleSlide7bPlay = () => {
    if (slide7bAudioPlaying) {
      // Pause
      if (slide7bAudioRef.current) slide7bAudioRef.current.pause();
      if (slide7bVideoRef.current) slide7bVideoRef.current.pause();
      if (slide7bTimeoutRef.current) clearTimeout(slide7bTimeoutRef.current);
      setSlide7bAudioPlaying(false);
    } else {
      // Play
      if (isPlaying) {
        if (audioRef.current) audioRef.current.pause();
        setIsPlaying(false);
      }
      if (slide7bAudioRef.current) {
        slide7bAudioRef.current.play();
        setSlide7bAudioPlaying(true);
        if (slide7bVideoRef.current) {
          if (slide7bVideoRef.current.currentTime === 0) {
            slide7bTimeoutRef.current = setTimeout(() => {
              if (slide7bVideoRef.current) {
                slide7bVideoRef.current.playbackRate = 0.5;
                slide7bVideoRef.current.play();
              }
            }, 10000);
          } else {
            slide7bVideoRef.current.playbackRate = 0.5;
            slide7bVideoRef.current.play();
          }
        }
      }
    }
  };
  useEffect(() => {
    const video = slide7VideoRef.current;
    if (!video) return;
    let reverseIntervalId: ReturnType<typeof setInterval>;
    let reverseTimeoutId: ReturnType<typeof setTimeout>;
    const reversePlay = () => {
      if (!video) return;
      if (video.currentTime <= 0.1) {
        // Reached the beginning, stop reversing and play forward again
        clearInterval(reverseIntervalId);
        video.playbackRate = 0.5;
        video.play();
        return;
      }
      // Only step back if the video isn't currently busy seeking
      // This prevents Chrome from stalling when stepping backwards
      if (!video.seeking) {
        // Step backwards by 0.05s every 100ms (0.5x speed)
        video.currentTime = Math.max(0, video.currentTime - 0.05);
      }
    };
    const handleEnded = () => {
      video.pause(); // Ensure forward playback is stopped
      // Wait 5 seconds before reversing
      reverseTimeoutId = setTimeout(() => {
        reverseIntervalId = setInterval(reversePlay, 100);
      }, 5000);
    };
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('ended', handleEnded);
      if (reverseIntervalId) clearInterval(reverseIntervalId);
      if (reverseTimeoutId) clearTimeout(reverseTimeoutId);
    };
  }, []);
  useEffect(() => {
    const video = slide7bVideoRef.current;
    if (!video) return;
    let reverseIntervalId: ReturnType<typeof setInterval>;
    let reverseTimeoutId: ReturnType<typeof setTimeout>;
    const reversePlay = () => {
      if (!video) return;
      if (video.currentTime <= 0.1) {
        // Reached the beginning, stop reversing and play forward again
        clearInterval(reverseIntervalId);
        video.playbackRate = 0.5;
        video.play();
        return;
      }
      // Only step back if the video isn't currently busy seeking
      // This prevents Chrome from stalling when stepping backwards
      if (!video.seeking) {
        // Step backwards by 0.05s every 100ms (0.5x speed)
        video.currentTime = Math.max(0, video.currentTime - 0.05);
      }
    };
    const handleEnded = () => {
      video.pause(); // Ensure forward playback is stopped
      // Wait 5 seconds before reversing
      reverseTimeoutId = setTimeout(() => {
        reverseIntervalId = setInterval(reversePlay, 100);
      }, 5000);
    };
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('ended', handleEnded);
      if (reverseIntervalId) clearInterval(reverseIntervalId);
      if (reverseTimeoutId) clearTimeout(reverseTimeoutId);
    };
  }, []);
  const tracks = ['https://cdn.magicpatterns.com/uploads/i6gGeaSuUDdxcvUXNfouoe/Dusk_at_the_Walled_Gate.mp3', 'https://cdn.magicpatterns.com/uploads/xqFvTV8Sq5Sxo7k3BZ1pu2/2._Maharaja_Ranjit_Singh_Invite_to_Enter_Quest__Lion_of_the_Five_Rivers.mp3'];
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const nextTrack = () => {
    setCurrentTrackIndex(prev => (prev + 1) % tracks.length);
    setIsPlaying(true);
  };
  useEffect(() => {
    if (audioRef.current && isPlaying) {
      audioRef.current.play().catch(() => setIsPlaying(false));
    }
  }, [currentTrackIndex]);
  const totalSlides = 27;
  const nextSlide = useCallback(() => {
    setActiveSlide(prev => Math.min(prev + 1, totalSlides - 1));
  }, []);
  const prevSlide = useCallback(() => {
    setActiveSlide(prev => Math.max(prev - 1, 0));
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  useEffect(() => {
    const archiveInterval = setInterval(() => {
      setArchiveIndex(prev => (prev + 1) % 4);
    }, 5000);
    const drinkInterval = setInterval(() => {
      setDrinkIndex(prev => (prev + 1) % 4);
    }, 3000);
    return () => {
      clearInterval(archiveInterval);
      clearInterval(drinkInterval);
    };
  }, []);
  return <div className="bg-black w-screen h-screen overflow-hidden text-white relative font-sans" data-id="element-7083">
      {/* Fixed Chrome */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-[#D4AF37] origin-left z-50" initial={{
      scaleX: 0
    }} animate={{
      scaleX: activeSlide / (totalSlides - 1)
    }} transition={{
      duration: 0.5,
      ease: 'easeInOut'
    }} data-id="element-7084" />

      <Link to="/" className="fixed top-8 left-8 z-40 flex items-center gap-3 text-white/50 hover:text-[#D4AF37] transition-colors text-xs tracking-widest uppercase font-light" data-id="element-7085">
        <ArrowLeft className="w-4 h-4" data-id="element-7086" />
        Home
      </Link>

      <div className="fixed top-8 right-8 z-40 flex flex-col items-end gap-3" data-id="element-7087">
        <div className="text-[#D4AF37]/60 text-xs font-light tracking-[0.3em]" data-id="element-7088">
          {String(activeSlide + 1).padStart(2, '0')} / {totalSlides}
        </div>
        <div className="flex items-center gap-2" data-id="element-7089">
          <button onClick={togglePlay} className="w-8 h-8 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 transition-all duration-300" data-id="element-7090">
            {isPlaying ? <Pause className="w-3 h-3" data-id="element-7091" /> : <Play className="w-3 h-3 ml-0.5" data-id="element-7092" />}
          </button>
          <button onClick={nextTrack} className="w-8 h-8 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 transition-all duration-300" data-id="element-7093">
            <SkipForward className="w-3 h-3" data-id="element-7094" />
          </button>
        </div>
      </div>

      <audio ref={audioRef} src={tracks[currentTrackIndex]} loop className="hidden" data-id="element-7095" />

      <div className="fixed bottom-8 right-8 z-40 flex items-center gap-4" data-id="element-7096">
        <button onClick={prevSlide} disabled={activeSlide === 0} className="w-12 h-12 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 disabled:opacity-20 transition-all duration-300" data-id="element-7097">
          <ArrowLeft className="w-5 h-5" data-id="element-7098" />
        </button>
        <button onClick={nextSlide} disabled={activeSlide === totalSlides - 1} className="w-12 h-12 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 disabled:opacity-20 transition-all duration-300" data-id="element-7099">
          <ArrowRight className="w-5 h-5" data-id="element-7100" />
        </button>
      </div>

      {/* Slides Container */}
      <motion.div className="flex h-full w-full" animate={{
      x: `-${activeSlide * 100}vw`
    }} transition={{
      type: 'spring',
      stiffness: 150,
      damping: 25,
      mass: 0.8
    }} data-id="element-7101">
        {/* 1. Cover */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7102">
          <SlideAtmosphere imageUrl="https://cdn.magicpatterns.com/uploads/7ngvtUG3Hhfrt7dJK3EcMQ/Gemini_Generated_Image_b3829nb3829nb382.png" opacity={0.5} overlayGradient="gold" animate="ken-burns-loop" withParticles data-id="element-7103" />
          <PunjabiPlacard word="ਸ਼ੁਰੂਆਤ" translation="Beginning" data-id="element-7104" />

          <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" data-id="element-7105">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} data-id="element-7106">
              <h1 className="font-serif text-6xl sm:text-7xl md:text-8xl text-[#FDFBF7] mb-6 leading-none drop-shadow-2xl" data-id="element-7107">
                Kingdoms of Punjab
              </h1>
              <p className="text-xl sm:text-2xl text-[#D4AF37] font-light tracking-[0.2em] uppercase mb-8" data-id="element-7108">
                Life in the Sikh Kingdoms
              </p>
              <div className="w-px h-16 bg-gradient-to-b from-[#D4AF37] to-transparent mx-auto" data-id="element-7109" />
            </motion.div>
          </div>
        </section>

        {/* 2. Opening Truth */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7110">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7111" />
          <PunjabiPlacard word="ਵਿਰਾਸਤ" translation="Heritage" data-id="element-7112" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7113">
            <motion.figure initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} className="mb-16" data-id="element-7114">
              <blockquote className="font-serif text-xl sm:text-2xl text-[#D4AF37] italic leading-relaxed" data-id="element-7115">
                "A people without knowledge of their past history, origin and
                culture is like a tree without roots."
              </blockquote>
              <figcaption className="text-white/40 text-xs tracking-[0.3em] uppercase mt-6" data-id="element-7116">
                — Winston Churchill
              </figcaption>
            </motion.figure>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            delay: 0.3
          }} data-id="element-7117">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7118">
                We inherited a civilization.
                <br data-id="element-7119" />
                <span className="text-[#D4AF37]" data-id="element-7120">
                  Our children inherited fragments.
                </span>
              </h2>
              <p className="text-lg text-white/60 font-light max-w-2xl mx-auto" data-id="element-7121">
                When a people forget their history, they risk losing the courage
                and dignity it was meant to give them.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 3. ROM Legacy */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7122">
          <SlideAtmosphere imageUrl="https://cdn.magicpatterns.com/uploads/mdkRi9opWdPpM6cSCo7Zgm/image.png" opacity={0.2} overlayGradient="dark" data-id="element-7123" />
          <PunjabiPlacard word="ਪ੍ਰਮਾਣ" translation="Proof" data-id="element-7124" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center gap-16" data-id="element-7125">
            <motion.div className="flex-1" initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7126">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-6" data-id="element-7127">
                The Precedent
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-6" data-id="element-7128">
                Our community has proven this story matters.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7129">
                <span className="text-[#D4AF37]" data-id="element-7130">
                  Arts of the Sikh Kingdoms
                </span>{' '}
                remains the second most successful exhibition in Canadian
                history at the Royal Ontario Museum.
              </p>
            </motion.div>
            <motion.div className="flex-1 w-full max-w-sm" initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7131">
              <div className="aspect-[3/4] p-3 bg-[#110c08] border border-[#D4AF37]/20 shadow-[0_20px_50px_rgba(0,0,0,0.5)]" data-id="element-7132">
                <img src="https://cdn.magicpatterns.com/uploads/mdkRi9opWdPpM6cSCo7Zgm/image.png" alt="ROM Exhibition" className="w-full h-full object-cover transition-all duration-700 hover:scale-105" data-id="element-7133" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 4. Epiphany & Trailer */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex flex-col items-center justify-center" data-id="element-7134">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7135" />
          <PunjabiPlacard word="ਜਾਗ੍ਰਿਤੀ" translation="Awakening" data-id="element-7136" />

          <div className="relative z-10 max-w-6xl mx-auto w-full px-6 text-center" data-id="element-7137">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7138">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-12" data-id="element-7139">
                What if Sikh history could feel alive?
              </h2>
              <div className="w-full max-w-4xl mx-auto aspect-video bg-black border border-[#D4AF37]/20 shadow-[0_0_50px_rgba(212,175,55,0.1)] relative p-1" data-id="element-7140">
                <iframe src="https://www.youtube.com/embed/2yMWvJinVgk" title="Trailer" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen" allowFullScreen className="w-full h-full" data-id="element-7141" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 5. Vision */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7142">
          <SlideAtmosphere imageUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png" opacity={0.15} overlayGradient="gold" data-id="element-7143" />
          <PunjabiPlacard word="ਮੰਜ਼ਿਲ" translation="Destination" data-id="element-7144" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7145">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7146">
              <p className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-8" data-id="element-7147">
                The Goal
              </p>
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7148">
                The most successful immersive experience in Canadian history.
              </h2>
              <div className="w-px h-12 bg-gradient-to-b from-[#D4AF37]/50 to-transparent mx-auto" data-id="element-7149" />
            </motion.div>
          </div>
        </section>

        {/* 6. Step Inside */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7150">
          <SlideAtmosphere videoUrl="https://cdn.magicpatterns.com/uploads/2vbujNeh6XCJnC6Xt6Yz87/animate_this_image_so_its_like.mp4" opacity={1} overlayGradient="dark" lightScrim videoBrightness={1.6} playbackRate={0.5} data-id="element-7151" />
          <PunjabiPlacard word="ਦਰਬਾਰ" translation="Court" data-id="element-7152" />

          <div className="absolute bottom-24 left-0 right-0 z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7153">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-7154">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#FDFBF7] leading-tight drop-shadow-[0_10px_30px_rgba(0,0,0,0.8)]" data-id="element-7155">
                Step into a world of empire, diplomacy, art, and courage.
              </h2>
            </motion.div>
          </div>
        </section>

        {/* 7. Portraits That Awaken */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7156">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7157" />
          <PunjabiPlacard word="ਦਰਸ਼ਨ" translation="Vision" data-id="element-7158" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 flex flex-col md:flex-row items-center gap-12" data-id="element-7159">
            <motion.div className="flex-1 text-center md:text-left" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7160">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-7161">
                Historical figures come to life.
              </h2>
              <p className="text-lg text-white/60 font-light mb-8" data-id="element-7162">
                Maharaja Ranjit Singh and others awaken on screen to guide
                students through the Darbar.
              </p>

              <div className="flex items-center gap-4" data-id="element-7163">
                <button onClick={handleSlide7Play} className="w-14 h-14 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/20 hover:scale-105 transition-all shrink-0" data-id="element-7164">
                  {slide7AudioPlaying ? <Pause className="w-6 h-6" data-id="element-7165" /> : <Play className="w-6 h-6 ml-1" data-id="element-7166" />}
                </button>
                <AnimatePresence data-id="element-7167">
                  {slide7AudioPlaying && <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -10
                }} className="flex flex-col" data-id="element-7168">
                      <p className="text-[#D4AF37] text-xs tracking-widest uppercase font-light mb-1" data-id="element-7169">
                        Now Playing
                      </p>
                      <p className="text-white/80 text-sm font-serif italic" data-id="element-7170">
                        Awakening of the Veiled Lioness
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
              <audio ref={slide7AudioRef} src="https://cdn.magicpatterns.com/uploads/fHMFP9bk9qh56JpvKMc9vM/Awakening_of_the_Veiled_Lioness_(2).mp3" className="hidden" data-id="element-7171" />
            </motion.div>
            <motion.div className="flex-1 w-full flex justify-center" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7172">
              <div className="h-[80vh] max-h-[800px] aspect-[9/16] relative p-4 bg-[#1a140f] border-[16px] border-[#8a6b22] shadow-[0_30px_60px_rgba(0,0,0,0.9),inset_0_0_30px_rgba(0,0,0,0.9)] rounded-sm before:absolute before:inset-0 before:border-[2px] before:border-[#D4AF37]/30 before:m-1.5" data-id="element-7173">
                <div className="w-full h-full relative overflow-hidden shadow-[inset_0_0_50px_rgba(0,0,0,0.9)] bg-black" data-id="element-7174">
                  <video ref={slide7VideoRef} src="https://cdn.magicpatterns.com/uploads/oAYvh93WVHg7sLuQmvLMws/WhatsApp%20Video%202026-06-08%20at%2009.07.35.mp4" muted playsInline className="w-full h-full object-cover" data-id="element-7175" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 7b. Paintings Become Portals */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7176">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7177" />
          <PunjabiPlacard word="ਕਲਾ" translation="Art" data-id="element-7178" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-7179">
            <motion.div className="flex-1 text-center md:text-left" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7180">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-7181">
                Paintings become portals.
              </h2>
              <p className="text-lg text-white/60 font-light mb-8" data-id="element-7182">
                Static archival art transforms into living, breathing figures
                right before their eyes.
              </p>

              <div className="flex items-center gap-4" data-id="element-7183">
                <button onClick={handleSlide7bPlay} className="w-14 h-14 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/20 hover:scale-105 transition-all shrink-0" data-id="element-7184">
                  {slide7bAudioPlaying ? <Pause className="w-6 h-6" data-id="element-7185" /> : <Play className="w-6 h-6 ml-1" data-id="element-7186" />}
                </button>
                <AnimatePresence data-id="element-7187">
                  {slide7bAudioPlaying && <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -10
                }} className="flex flex-col" data-id="element-7188">
                      <p className="text-[#D4AF37] text-xs tracking-widest uppercase font-light mb-1" data-id="element-7189">
                        Now Playing
                      </p>
                      <p className="text-white/80 text-sm font-serif italic" data-id="element-7190">
                        Awakening of the Maharaja
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
              <audio ref={slide7bAudioRef} src="https://cdn.magicpatterns.com/uploads/6aVtpiwFTh4Fdg7GirPpMC/Awakening_of_the_Maharaja.mp3" className="hidden" data-id="element-7191" />
            </motion.div>
            <motion.div className="flex-1 w-full flex justify-center" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7192">
              <div className="h-[80vh] max-h-[800px] aspect-[9/16] relative p-4 bg-[#1a140f] border-[16px] border-[#8a6b22] shadow-[0_30px_60px_rgba(0,0,0,0.9),inset_0_0_30px_rgba(0,0,0,0.9)] rounded-sm before:absolute before:inset-0 before:border-[2px] before:border-[#D4AF37]/30 before:m-1.5" data-id="element-7193">
                <div className="w-full h-full relative overflow-hidden shadow-[inset_0_0_50px_rgba(0,0,0,0.9)] bg-black" data-id="element-7194">
                  <video ref={slide7bVideoRef} src="https://cdn.magicpatterns.com/uploads/4rTD4YeXmjgXGTejYDnVus/WhatsApp%20Video%202026-05-21%20at%2016.14.44%20(1).mp4" muted playsInline className="w-full h-full object-cover" data-id="element-7195" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 8. History Speaks Back */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7196">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7197" />
          <PunjabiPlacard word="ਸੰਵਾਦ" translation="Dialogue" data-id="element-7198" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-7199">
            <motion.div className="flex-1 text-center md:text-left" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7200">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-7201">
                From watching history to speaking with it.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7202">
                Using live avatar technology, students ask questions and receive
                historically informed responses in real time.
              </p>
            </motion.div>
            <motion.div className="flex-1 w-full flex justify-center" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7203">
              <div className="h-[80vh] max-h-[800px] aspect-[9/16] relative p-4 bg-[#1a140f] border-[16px] border-[#8a6b22] shadow-[0_30px_60px_rgba(0,0,0,0.9),inset_0_0_30px_rgba(0,0,0,0.9)] rounded-sm before:absolute before:inset-0 before:border-[2px] before:border-[#D4AF37]/30 before:m-1.5" data-id="element-7204">
                <div className="w-full h-full relative overflow-hidden shadow-[inset_0_0_50px_rgba(0,0,0,0.9)] bg-black" data-id="element-7205">
                  <video src="https://cdn.magicpatterns.com/uploads/tHe5vNNjxpJUtnsEoB8t32/WhatsApp%20Video%202026-05-26%20at%2015.06.40.mp4" playsInline controls className="w-full h-full object-cover" data-id="element-7206" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 9. Living Archive */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7207">
          <div className="absolute inset-0 overflow-hidden" data-id="element-7208">
            <AnimatePresence mode="popLayout" data-id="element-7209">
              <motion.img key={archiveIndex} src={['https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2uRFx3az1Ndo6QPmwLotNT/Gemini_Generated_Image_6xdim76xdim76xdi.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/wVBxvpncFWjZ3U7V1C4TbV/Gemini_Generated_Image_cc8os5cc8os5cc8o.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/iav6C11v1iAqRNyzsLVGur/Gemini_Generated_Image_ouvfm7ouvfm7ouvf.png'][archiveIndex]} initial={{
              opacity: 0,
              x: 100
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -100
            }} transition={{
              duration: 1.5,
              ease: 'easeInOut'
            }} className="absolute inset-0 w-full h-full object-cover" data-id="element-7210" />
            </AnimatePresence>
          </div>
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7211" />
          <PunjabiPlacard word="ਯਾਦ" translation="Memory" data-id="element-7212" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7213">
            <motion.div className="text-center" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7214">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] mb-6 drop-shadow-2xl" data-id="element-7215">
                A living archive of memories.
              </h2>
              <p className="text-xl text-white/90 font-light drop-shadow-lg" data-id="element-7216">
                Cinematic scenes of battles, diplomacy, and sacred spaces.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 10. Scent, Sound, Taste */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7217">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7218" />
          <PunjabiPlacard word="ਅਹਿਸਾਸ" translation="Feeling" data-id="element-7219" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7220">
            <motion.div className="text-center mb-16" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7221">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7222">
                Not just seen. Felt.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7223">
                Sound, scent, and taste make history unforgettable.
              </p>
            </motion.div>

            <div className="flex justify-center items-center h-[50vh]" data-id="element-7224">
              {[{
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/e7tNmw42aGJMhG2K8pg9sq/Sher-e-Punjab_Lassi_%28Lion_of_Punjab_Smoothie%29.webp',
              name: 'Sher-e-Punjab Lassi',
              inspiredBy: 'Inspired by Maharaja Ranjit Singh'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7s7gbBG7Kx151cwzpHK1du/Hari_Singh_Nalwa%E2%80%99s_Tiger_Fury_Sharbat.webp',
              name: 'Tiger Fury Sharbat',
              inspiredBy: 'Inspired by Hari Singh Nalwa'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eKo28eax4tv8HfFsN3KZGY/Chabeel_of_Compassion_%28Rose__Kewra_Cooler%29.webp',
              name: 'Chabeel of Compassion',
              inspiredBy: 'Inspired by Moran Sarkar'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uNnPb6Ex8V8D1T89ouiXSm/Shahi_Aam_Panna_%28Royal_Raw_Mango_Punch%29.webp',
              name: 'Shahi Aam Panna',
              inspiredBy: 'Inspired by Maharani Jind Kaur'
            }].map((drink, i) => {
              const isCenter = i === drinkIndex;
              const offset = (i - drinkIndex + 4) % 4;
              const position = offset === 3 ? -1 : offset;
              return <motion.div key={i} animate={{
                x: position * 250,
                scale: isCenter ? 1.2 : 0.8,
                opacity: isCenter ? 1 : 0.4,
                zIndex: isCenter ? 20 : 10
              }} transition={{
                duration: 0.8,
                ease: 'easeInOut'
              }} className="absolute text-center w-48 sm:w-56" data-id="element-7225">
                    <div className="aspect-[3/4] mb-5 overflow-hidden border border-[#D4AF37]/40 bg-[#0a0604] relative shadow-2xl" data-id="element-7226">
                      <img src={drink.src} alt={drink.name} className="w-full h-full object-cover" data-id="element-7227" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" data-id="element-7228" />
                    </div>
                    <motion.div animate={{
                  opacity: isCenter ? 1 : 0
                }} transition={{
                  duration: 0.5
                }} data-id="element-7229">
                      <p className="text-[#D4AF37] text-sm tracking-widest uppercase mb-2 font-medium drop-shadow-md" data-id="element-7230">
                        {drink.name}
                      </p>
                      <p className="text-white/80 text-xs italic font-light drop-shadow-md" data-id="element-7231">
                        {drink.inspiredBy}
                      </p>
                    </motion.div>
                  </motion.div>;
            })}
            </div>
          </div>
        </section>

        {/* 11. In Progress Now */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7232">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7233" />
          <PunjabiPlacard word="ਸਿਰਜਣਾ" translation="Creation" data-id="element-7234" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full" data-id="element-7235">
            <motion.div className="text-center mb-12" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7236">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7237">
                This is no longer just an idea.
              </h2>
              <p className="text-lg text-white/60 font-light mb-2" data-id="element-7238">
                Testing the environment, scale, and production pathway.
              </p>
              <p className="text-xs text-[#D4AF37] tracking-widest uppercase font-light" data-id="element-7239">
                Live footage from construction at Illuminarium at the
                Distillery, June 2026.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-6" data-id="element-7240">
              {['https://cdn.magicpatterns.com/uploads/a7heywR1FeDYnub9ua9WT9/WhatsApp_Image_2026-06-12_at_14.36.03_(1).jpg', 'https://cdn.magicpatterns.com/uploads/ssJFUVqUnzkDMnb4yqvNvT/WhatsApp_Image_2026-06-13_at_10.33.54.jpg', 'https://cdn.magicpatterns.com/uploads/kQxbd1wr2FNT6U2x9evwFs/WhatsApp_Image_2026-06-12_at_14.36.03_(6).jpg', 'https://cdn.magicpatterns.com/uploads/pnghcT8rVnDqxy4q3pgpBM/WhatsApp_Image_2026-06-12_at_14.36.03_(7).jpg'].map((src, i) => <motion.div key={i} onClick={() => setSelectedImage(src)} initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: i * 0.1
            }} className="aspect-video bg-[#0a0604] border border-[#D4AF37]/20 relative overflow-hidden cursor-pointer group" data-id="element-7241">
                  <img src={src} alt="Progress" className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700" data-id="element-7242" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center" data-id="element-7243">
                    <span className="opacity-0 group-hover:opacity-100 text-white/90 text-xs tracking-widest uppercase font-light transition-opacity duration-500" data-id="element-7244">
                      Enlarge
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 12. Pathway — Bramalea pilot */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7245">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7246" />
          <PunjabiPlacard word="ਸਿੱਖਿਆ" translation="Education" data-id="element-7247" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-16" data-id="element-7248">
            {/* Left: the story */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7249">
              <span className="inline-flex items-center gap-2 text-[#D4AF37] text-[0.65rem] tracking-[0.3em] uppercase font-light mb-5" data-id="element-7250">
                <span className="w-8 h-px bg-[#D4AF37]/60" data-id="element-7251" />
                The Pilot
              </span>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-[1.05] mb-6" data-id="element-7252">
                We start where it's
                <br data-id="element-7253" />
                hardest — on purpose.
              </h2>
              <p className="text-lg text-white/70 font-light leading-relaxed mb-6" data-id="element-7254">
                Our first pilot launches at{' '}
                <span className="text-[#D4AF37]" data-id="element-7255">
                  Bramalea Secondary School
                </span>
                , in one of Brampton's least affluent neighbourhoods — the
                students who are too often left out of the story.
              </p>
              <p className="text-lg text-white/70 font-light leading-relaxed mb-8" data-id="element-7256">
                Prove that heritage can light up a classroom{' '}
                <span className="text-[#FDFBF7]" data-id="element-7257">here</span>, and we prove it
                can work anywhere.
              </p>

              {/* Expansion ladder */}
              <div className="flex items-center gap-3 flex-wrap" data-id="element-7258">
                {[{
                label: 'Bramalea',
                sub: '1 school'
              }, {
                label: 'Peel Region',
                sub: 'Regional'
              }, {
                label: 'Canada',
                sub: 'National'
              }].map((step, i) => <div key={step.label} className="flex items-center gap-3" data-id="element-7259">
                    <div className={`px-4 py-2 border ${i === 0 ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/25 bg-black/30'}`} data-id="element-7260">
                      <span className={`block text-sm font-serif ${i === 0 ? 'text-[#D4AF37]' : 'text-white/80'}`} data-id="element-7261">
                        {step.label}
                      </span>
                      <span className="block text-[0.6rem] tracking-widest uppercase text-white/40 font-light" data-id="element-7262">
                        {step.sub}
                      </span>
                    </div>
                    {i < 2 && <ArrowRight className="w-4 h-4 text-[#D4AF37]/50 flex-shrink-0" data-id="element-7263" />}
                  </div>)}
              </div>
            </motion.div>

            {/* Right: the school + quote */}
            <motion.div className="w-full" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1,
            delay: 0.2
          }} data-id="element-7264">
              <div className="relative overflow-hidden border border-[#D4AF37]/20 shadow-2xl" data-id="element-7265">
                <img src="https://cdn.magicpatterns.com/uploads/tt3kwmA1Jc3p3kSKo745wm/pasted-image.webp" alt="Bramalea Secondary School, 510 Balmoral Drive, Brampton" className="w-full h-56 sm:h-64 object-cover" data-id="element-7266" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" data-id="element-7267" />
                <div className="absolute bottom-0 left-0 p-4" data-id="element-7268">
                  <p className="text-[#FDFBF7] text-sm font-serif" data-id="element-7269">
                    Bramalea Secondary School
                  </p>
                  <p className="text-white/50 text-[0.65rem] tracking-widest uppercase font-light" data-id="element-7270">
                    Brampton, Ontario
                  </p>
                </div>
              </div>

              <div className="border-l-[2px] border-[#D4AF37]/50 pl-6 mt-8 relative" data-id="element-7271">
                <div className="absolute -left-[1px] top-0 w-[2px] h-10 bg-[#D4AF37]" data-id="element-7272" />
                <p className="font-serif text-xl sm:text-2xl text-[#D4AF37] mb-4 leading-relaxed italic" data-id="element-7273">
                  "Our community does not get enough space in historical
                  education. This is a powerful opportunity."
                </p>
                <p className="text-white/40 text-[0.65rem] tracking-widest uppercase font-light" data-id="element-7274">
                  — Principal, Bramalea Secondary School
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 13. Treasury Cards Fan */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7275">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7276" />
          <PunjabiPlacard word="ਖ਼ਜ਼ਾਨਾ" translation="Treasury" data-id="element-7277" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full flex flex-col items-center justify-center h-full py-4" data-id="element-7278">
            <motion.div className="text-center mb-2" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7279">
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-none mb-2 tracking-wide uppercase" data-id="element-7280">
                Treasures of the{' '}
                <span className="text-[#D4AF37]" data-id="element-7281">Sikh Empire</span>
              </h2>
              <p className="text-xs sm:text-sm text-white/60 tracking-[0.2em] uppercase font-light" data-id="element-7282">
                Recovering the lost stories of the Sikh Kingdoms.
              </p>
            </motion.div>

            <TreasuryFan cards={[{
            src: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
            name: 'Hari Singh Nalwa',
            desc: 'The legendary commander of the Sikh Khalsa Army.',
            stats: [{
              label: 'Rarity',
              value: 'Legendary'
            }, {
              label: 'Type',
              value: 'Figure'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png',
            name: 'Harmandir Sahib',
            desc: 'The golden spiritual heart of the Sikh faith.',
            stats: [{
              label: 'Rarity',
              value: 'Mythic'
            }, {
              label: 'Type',
              value: 'Place'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/jyrMYyNM9JskmMzEnBrLTw/Koh-i-noor_Armlet.png',
            name: 'Koh-i-Noor Armlet',
            desc: 'The fabled diamond set in the Maharaja’s armlet.',
            stats: [{
              label: 'Rarity',
              value: 'Mythic'
            }, {
              label: 'Type',
              value: 'Ornament'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/itiARdFbZcdkmdrUEtYH65/Golden_Throne_of_Lahore.png',
            name: 'Golden Throne of Lahore',
            desc: 'The gilded seat of power of the Sikh Empire.',
            stats: [{
              label: 'Rarity',
              value: 'Legendary'
            }, {
              label: 'Type',
              value: 'Artifact'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
            name: 'Ranjit Singh',
            desc: 'The Lion of Punjab, founder of the Sikh Empire.',
            stats: [{
              label: 'Rarity',
              value: 'Legendary'
            }, {
              label: 'Type',
              value: 'Ruler'
            }]
          }]} data-id="element-7283" />

            <motion.div className="text-center mt-10 max-w-3xl mx-auto" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.3
          }} data-id="element-7284">
              <div className="flex items-center justify-center gap-4 mb-3" data-id="element-7285">
                <div className="w-12 h-px bg-[#D4AF37]/30" data-id="element-7286" />
                <Star className="w-4 h-4 text-[#D4AF37]" data-id="element-7287" />
                <div className="w-12 h-px bg-[#D4AF37]/30" data-id="element-7288" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] mb-2" data-id="element-7289">
                Real History.{' '}
                <span className="text-[#D4AF37]" data-id="element-7290">Real Connection.</span>
              </h3>
              <p className="text-base sm:text-lg text-white/80 font-serif italic leading-relaxed" data-id="element-7291">
                "A digital bridge to the physical past. Every artifact and
                figure is <span className="text-[#D4AF37]" data-id="element-7292">real history</span>.
                You aren't just playing a game — you are{' '}
                <span className="text-[#D4AF37]" data-id="element-7293">recovering a lost legacy</span>{' '}
                and building a modern Sangat."
              </p>
            </motion.div>
          </div>
        </section>

        {/* 13b. Daily Packet */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7294">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7295" />
          <PunjabiPlacard word="ਰੋਜ਼ਾਨਾ" translation="Daily" data-id="element-7296" />

          <div className="relative z-10 max-w-4xl mx-auto px-6 w-full flex flex-col items-center" data-id="element-7297">
            <motion.div className="text-center mb-16" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7298">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] mb-6 tracking-wide uppercase" data-id="element-7299">
                The Daily <span className="text-[#D4AF37]" data-id="element-7300">Packet</span>
              </h2>
              <p className="text-lg text-white/70 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-7301">
                Every 24 hours, a sealed leather envelope arrives from the
                Darbar. Open it to reveal new artifacts, and answer the optional
                Court Question for bonus influence.
              </p>
            </motion.div>

            <div className="mb-16 relative h-64 flex items-center justify-center" data-id="element-7302">
              <DailyPacketAnimation data-id="element-7303" />
            </div>

            <motion.div className="text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.2
          }} data-id="element-7304">
              <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-7305">
                Pokémon meets Magic: The Gathering meets Sikh history.
              </h3>
              <p className="text-lg text-white/60 font-light" data-id="element-7306">
                Students learn by collecting, unlocking, and competing.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 14. School to Show */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7307">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7308" />
          <PunjabiPlacard word="ਸਫ਼ਰ" translation="Journey" data-id="element-7309" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7310">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7311">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-12" data-id="element-7312">
                First they unlock the story.
                <br data-id="element-7313" />
                <span className="text-[#D4AF37]" data-id="element-7314">
                  Then they step inside it.
                </span>
              </h2>
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs tracking-[0.2em] uppercase font-light text-white/50" data-id="element-7315">
                <span data-id="element-7316">School</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7317">→</span>
                <span data-id="element-7318">Cards</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7319">→</span>
                <span data-id="element-7320">Bus</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7321">→</span>
                <span className="text-[#D4AF37]" data-id="element-7322">Immersive Show</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Sneak Peek (moved after School to Show) */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7323">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7324" />
          <PunjabiPlacard word="ਝਲਕ" translation="Glimpse" data-id="element-7325" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 text-center" data-id="element-7326">
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7327">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-8" data-id="element-7328">
                A glimpse of the experience.
              </h2>
              <div className="aspect-video bg-black border border-[#D4AF37]/20 shadow-[0_0_50px_rgba(212,175,55,0.1)] relative p-1" data-id="element-7329">
                <iframe src="https://www.youtube.com/embed/pW6rFd_Td0o?controls=0&rel=0&modestbranding=1&showinfo=0" title="Sneak Peek" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen" allowFullScreen className="w-full h-full" data-id="element-7330" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 15. Access Problem */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7331">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7332" />
          <PunjabiPlacard word="ਪਹੁੰਚ" translation="Access" data-id="element-7333" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7334">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-7335">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7336">
                The barrier should not be a bus.
              </h2>
              <p className="text-lg text-white/60 font-light max-w-2xl mx-auto" data-id="element-7337">
                If we want this to reach the next generation, we must remove the
                financial barriers to transportation and access.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 16. First Year Goal */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7338">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7339" />
          <PunjabiPlacard word="ਨਿਸ਼ਾਨਾ" translation="Target" data-id="element-7340" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7341">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7342">
              <h2 className="font-serif text-7xl sm:text-8xl text-[#FDFBF7] leading-none mb-4" data-id="element-7343">
                12,000
              </h2>
              <p className="text-xl tracking-[0.3em] uppercase text-[#D4AF37] font-light mb-8" data-id="element-7344">
                Students in Year One
              </p>
              <div className="w-px h-12 bg-gradient-to-b from-[#D4AF37]/50 to-transparent mx-auto" data-id="element-7345" />
            </motion.div>
          </div>
        </section>

        {/* 18. National Moments */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7346">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7347" />
          <PunjabiPlacard word="ਲਹਿਰ" translation="Movement" data-id="element-7348" />

          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center" data-id="element-7349">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-7350">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-12" data-id="element-7351">
                This becomes a Canadian cultural moment.
              </h2>
              <div className="flex flex-col sm:flex-row gap-8 justify-center text-left" data-id="element-7352">
                <div className="flex-1 border-t border-[#D4AF37]/30 pt-6" data-id="element-7353">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-3" data-id="element-7354">
                    Nov 2026
                  </p>
                  <p className="text-white/70 font-light text-sm" data-id="element-7355">
                    Premier of Ontario & Provincial Government
                  </p>
                </div>
                <div className="flex-1 border-t border-[#D4AF37]/30 pt-6" data-id="element-7356">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-3" data-id="element-7357">
                    Apr 2027
                  </p>
                  <p className="text-white/70 font-light text-sm" data-id="element-7358">
                    Prime Minister & Federal Government in Ottawa
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 19. Ask Context */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7359">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7360" />
          <PunjabiPlacard word="ਸਰਪ੍ਰਸਤ" translation="Patron" data-id="element-7361" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7362">
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7363">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7364">
                Become a Founding Patron of the Sikh Kingdoms.
              </h2>
              <p className="text-xl text-white/70 font-light mb-12" data-id="element-7365">
                We have committed{' '}
                <span className="text-[#D4AF37]" data-id="element-7366">$500,000</span> to begin. We
                ask the community to help carry it forward.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 text-left max-w-2xl mx-auto" data-id="element-7367">
                {['Sponsor your school', 'Fund student access', 'Production upgrades', 'National expansion'].map((item, i) => <div key={i} className="flex items-center gap-3 border border-[#D4AF37]/20 bg-black/40 p-4 backdrop-blur-sm" data-id="element-7368">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" data-id="element-7369" />
                    <span className="text-white/70 text-sm font-light" data-id="element-7370">
                      {item}
                    </span>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </section>

        {/* 20. Ask Tiers */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7371">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7372" />
          <PunjabiPlacard word="ਵਿਰਸਾ" translation="Legacy" data-id="element-7373" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7374">
            <div className="text-center mb-10" data-id="element-7375">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7376">
                Choose your legacy.
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10" data-id="element-7377">
              {[{
              price: '$10,000',
              title: 'Patron of the Bus',
              icon: Building2,
              items: ['Sponsor student transportation', 'Recognition on sponsor night', 'Invite a school']
            }, {
              price: '$25,000',
              title: 'Founding School Patron',
              icon: Users,
              items: ['Sponsor a school activation', 'Family banner on your night', 'Recognition inside experience']
            }, {
              price: '$50,000',
              title: 'Legacy Patron',
              icon: Crown,
              items: ['Support access & expansion', 'Premium family recognition', 'Maharaja acknowledgement moment']
            }].map((tier, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: i * 0.1
            }} className="border border-[#D4AF37]/30 bg-[#0a0604]/80 p-6 backdrop-blur-md relative overflow-hidden" data-id="element-7378">
                  <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-7379">
                    <tier.icon className="w-20 h-20 text-[#D4AF37]" data-id="element-7380" />
                  </div>
                  <h3 className="font-serif text-3xl text-[#FDFBF7] mb-2" data-id="element-7381">
                    {tier.price}
                  </h3>
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-6" data-id="element-7382">
                    {tier.title}
                  </p>
                  <ul className="space-y-3" data-id="element-7383">
                    {tier.items.map((item, j) => <li key={j} className="text-white/60 text-sm font-light flex gap-2" data-id="element-7384">
                        <span className="text-[#D4AF37] opacity-50" data-id="element-7385">•</span>{' '}
                        {item}
                      </li>)}
                  </ul>
                </motion.div>)}
            </div>

            <div className="text-center flex flex-col items-center" data-id="element-7386">
              <div className="bg-white p-2 rounded-sm shadow-[0_0_30px_rgba(212,175,55,0.3)] mb-4" data-id="element-7387">
                <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://typeform.com" alt="Scan to commit" className="w-20 h-20" data-id="element-7388" />
              </div>
              <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-7389">
                Scan to commit
              </p>
              <p className="text-white/40 text-[10px] italic font-light" data-id="element-7390">
                Contributions may be eligible for tax receipts where applicable.
              </p>
            </div>
          </div>
        </section>

        {/* 21. FAQ */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7391">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7392" />
          <PunjabiPlacard word="ਗਿਆਨ" translation="Knowledge" data-id="element-7393" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full" data-id="element-7394">
            <div className="text-center mb-10" data-id="element-7395">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7396">
                Giving &amp; tax, simply.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7397">
                A plain-language guide to how contributions are receipted.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-7398">
              <div className="border border-[#D4AF37]/20 bg-[#0a0604]/60 p-6 backdrop-blur-sm" data-id="element-7399">
                <div className="flex items-center gap-3 mb-4" data-id="element-7400">
                  <Receipt className="w-5 h-5 text-[#D4AF37]" data-id="element-7401" />
                  <h3 className="font-serif text-xl text-[#FDFBF7]" data-id="element-7402">
                    What might a $10,000 gift cost me?
                  </h3>
                </div>
                <p className="text-sm text-white/70 font-light leading-relaxed mb-4" data-id="element-7403">
                  An Ontario donor contributing{' '}
                  <span className="text-[#FDFBF7]" data-id="element-7404">$10,000</span> to a
                  registered charity may receive roughly{' '}
                  <span className="text-[#D4AF37]" data-id="element-7405">$3,975–$4,000</span> in
                  combined federal and provincial charitable tax credits —
                  making the <span className="text-[#FDFBF7]" data-id="element-7406">estimated</span>{' '}
                  after-tax cost about{' '}
                  <span className="text-[#D4AF37]" data-id="element-7407">$6,000</span>.
                </p>
                <p className="text-[10px] text-white/40 italic leading-relaxed" data-id="element-7408">
                  An estimate, not a guaranteed refund. Charitable credits are
                  non-refundable: they reduce taxes you owe, and depend on your
                  personal tax situation and the eligible receiptable amount.
                </p>
              </div>

              <div className="border border-[#D4AF37]/20 bg-[#0a0604]/60 p-6 backdrop-blur-sm" data-id="element-7409">
                <div className="flex items-center gap-3 mb-4" data-id="element-7410">
                  <Info className="w-5 h-5 text-[#D4AF37]" data-id="element-7411" />
                  <h3 className="font-serif text-xl text-[#FDFBF7]" data-id="element-7412">
                    How the credit works
                  </h3>
                </div>
                <ul className="space-y-3 text-sm text-white/70 font-light" data-id="element-7413">
                  <li data-id="element-7414">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7415">•</span>First $200
                    credited at ~20%; remainder at ~40%.
                  </li>
                  <li data-id="element-7416">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7417">•</span>Credits apply
                    only up to tax otherwise payable.
                  </li>
                  <li data-id="element-7418">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7419">•</span>Claimable up
                    to 75% of net income.
                  </li>
                  <li data-id="element-7420">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7421">•</span>Unused amounts
                    carry forward up to 5 years.
                  </li>
                </ul>
              </div>
            </div>

            <div className="border border-[#D4AF37]/20 bg-[#0a0604]/60 p-6 backdrop-blur-sm" data-id="element-7422">
              <div className="flex items-center gap-3 mb-4" data-id="element-7423">
                <FileText className="w-5 h-5 text-[#D4AF37]" data-id="element-7424" />
                <h3 className="font-serif text-xl text-[#FDFBF7]" data-id="element-7425">
                  Receipting Structure
                </h3>
              </div>
              <div className="grid sm:grid-cols-3 gap-6" data-id="element-7426">
                <div data-id="element-7427">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-2" data-id="element-7428">
                    Donation
                  </p>
                  <p className="text-xs text-white/60 font-light" data-id="element-7429">
                    Charitable gift with no material benefit. Full receipt.
                  </p>
                </div>
                <div data-id="element-7430">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-2" data-id="element-7431">
                    Sponsorship
                  </p>
                  <p className="text-xs text-white/60 font-light" data-id="element-7432">
                    Marketing value. Business expense, not a charitable receipt.
                  </p>
                </div>
                <div data-id="element-7433">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-2" data-id="element-7434">
                    Hybrid
                  </p>
                  <p className="text-xs text-white/60 font-light" data-id="element-7435">
                    Partial receipt only for amount above fair-market value of
                    benefits.
                  </p>
                </div>
              </div>
              <p className="text-[10px] text-white/40 italic mt-6 flex items-start gap-2" data-id="element-7436">
                <HandCoins className="w-3 h-3 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-7437" />
                If you receive tickets, dinners, or VIP access, CRA treats their
                value as an advantage — the receipt covers only the eligible
                gift after subtracting that value. This is general information,
                not tax advice; please confirm with your advisor.
              </p>
            </div>
          </div>
        </section>

        {/* 22. Why Ash Singh */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7438">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7439" />
          <PunjabiPlacard word="ਭਰੋਸਾ" translation="Trust" data-id="element-7440" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7441">
            <div className="text-center mb-12" data-id="element-7442">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-7443">
                Why I Feel Called To Do This Now
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] max-w-4xl mx-auto" data-id="element-7444">
                This is not about being the centre of the project. It is about
                bringing this history forward, with care.
              </h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-7445">
              <div data-id="element-7446">
                <p className="text-lg text-white/80 font-light mb-6" data-id="element-7447">
                  Using whatever experience, relationships, teaching background,
                  and energy I have to help carry this story forward. I have
                  spent years building{' '}
                  <span className="text-[#FDFBF7]" data-id="element-7448">
                    Sikh education, digital learning, and community projects
                  </span>
                  . Now feels like the moment to bring those threads together in
                  service of something larger.
                </p>
                <ul className="space-y-3 text-sm text-white/60 font-light" data-id="element-7449">
                  <li data-id="element-7450">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7451">•</span>Founder of
                    SikhiWiki (65M+ visitors) & Turbanizer
                  </li>
                  <li data-id="element-7452">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7453">•</span>Global
                    entrepreneurship educator (INSEAD, Ivey, Wharton)
                  </li>
                  <li data-id="element-7454">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7455">•</span>Son of the
                    leaders behind Arts of the Sikh Kingdoms
                  </li>
                  <li data-id="element-7456">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7457">•</span>Lead sevadar
                    supporting the Sikh Art & Culture Gallery at ROM
                  </li>
                  <li data-id="element-7458">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7459">•</span>Producer of
                    large-scale community experiences & TV
                  </li>
                  <li data-id="element-7460">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7461">•</span>Former owner
                    of Singapore's pro basketball team
                  </li>
                  <li data-id="element-7462">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7463">•</span>Startup
                    professor, coder, and AI builder
                  </li>
                </ul>
              </div>

              <div className="flex justify-center" data-id="element-7464">
                <div className="relative w-[300px] h-[300px]" data-id="element-7465">
                  <div className="absolute inset-0 rounded-full border border-[#D4AF37]/20 animate-[spin_60s_linear_infinite]" data-id="element-7466" />
                  <div className="absolute inset-8 rounded-full border border-[#D4AF37]/10 animate-[spin_40s_linear_infinite_reverse]" data-id="element-7467" />

                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" data-id="element-7468">
                    <div className="w-24 h-24 rounded-full bg-[#110c08] border border-[#D4AF37]/50 flex flex-col items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.2)]" data-id="element-7469">
                      <Crown className="w-6 h-6 text-[#D4AF37] mb-1" data-id="element-7470" />
                      <p className="font-serif text-[#FDFBF7] text-sm" data-id="element-7471">
                        Ash Singh
                      </p>
                    </div>
                  </div>

                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 text-center" data-id="element-7472">
                    <div className="bg-[#0a0604]/80 border border-[#D4AF37]/30 rounded p-2 backdrop-blur-sm" data-id="element-7473">
                      <Globe className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" data-id="element-7474" />
                      <p className="text-[8px] text-[#D4AF37] uppercase tracking-widest" data-id="element-7475">
                        Digital Seva
                      </p>
                    </div>
                  </div>
                  <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 w-24 text-center" data-id="element-7476">
                    <div className="bg-[#0a0604]/80 border border-[#D4AF37]/30 rounded p-2 backdrop-blur-sm" data-id="element-7477">
                      <GraduationCap className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" data-id="element-7478" />
                      <p className="text-[8px] text-[#D4AF37] uppercase tracking-widest" data-id="element-7479">
                        Education
                      </p>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-24 text-center" data-id="element-7480">
                    <div className="bg-[#0a0604]/80 border border-[#D4AF37]/30 rounded p-2 backdrop-blur-sm" data-id="element-7481">
                      <Landmark className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" data-id="element-7482" />
                      <p className="text-[8px] text-[#D4AF37] uppercase tracking-widest" data-id="element-7483">
                        Culture
                      </p>
                    </div>
                  </div>
                  <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 w-24 text-center" data-id="element-7484">
                    <div className="bg-[#0a0604]/80 border border-[#D4AF37]/30 rounded p-2 backdrop-blur-sm" data-id="element-7485">
                      <Sparkles className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" data-id="element-7486" />
                      <p className="text-[8px] text-[#D4AF37] uppercase tracking-widest" data-id="element-7487">
                        Experience
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-16" data-id="element-7488">
              <p className="font-serif text-xl sm:text-2xl text-[#FDFBF7]" data-id="element-7489">
                This is not a side project.{' '}
                <span className="text-[#D4AF37]" data-id="element-7490">
                  This is the work I have been preparing for my whole life.
                </span>
              </p>
            </div>
          </div>
        </section>

        {/* 23. Cartier Collaboration - Magazine */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7491">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7492" />
          <PunjabiPlacard word="ਵਿਰਾਸਤ" translation="Legacy" data-id="element-7493" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full grid lg:grid-cols-2 gap-12 items-center" data-id="element-7494">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7495">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-7496">
                A Legacy of Cultural Exchange
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6 leading-tight" data-id="element-7497">
                The Sikh Empire & <br data-id="element-7498" />
                <span className="text-[#D4AF37]" data-id="element-7499">The House of Cartier</span>
              </h2>
              <div className="space-y-6 text-white/80 font-light text-lg" data-id="element-7500">
                <p data-id="element-7501">
                  Maharaja Ranjit Singh’s court was globally connected,
                  maintaining diplomatic relations with the King of France.
                  French generals trained the Khalsa army, bringing European
                  military precision to the Punjab.
                </p>
                <p data-id="element-7502">
                  The Sikh Empire collapsed in{' '}
                  <span className="text-[#FDFBF7] font-medium" data-id="element-7503">1849</span>. The
                  House of Cartier was founded in Paris in{' '}
                  <span className="text-[#FDFBF7] font-medium" data-id="element-7504">1847</span>.
                </p>
                <p className="font-serif italic text-xl text-[#D4AF37] border-l-2 border-[#D4AF37]/30 pl-4 py-2" data-id="element-7505">
                  For two years, these two empires overlapped in time.
                </p>
                <p data-id="element-7506">
                  Ever since, Cartier has been the jeweler of choice for the
                  Maharajas, setting the world's most famous diamonds —
                  including those that once belonged to the Lahore Treasury.
                </p>
              </div>
            </motion.div>

            <motion.div className="relative flex justify-center" initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 0.2
          }} data-id="element-7507">
              <div className="relative w-full max-w-md aspect-[2/3] cursor-pointer group" onClick={() => setSelectedImage('https://cdn.magicpatterns.com/uploads/oQ58wnU7hUNtNqVh7jGTxZ/Gemini_Generated_Image_ep18z9ep18z9ep18.png')} data-id="element-7508">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#D4AF37]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" data-id="element-7509" />
                <img src="https://cdn.magicpatterns.com/uploads/oQ58wnU7hUNtNqVh7jGTxZ/Gemini_Generated_Image_ep18z9ep18z9ep18.png" alt="Cartier Magazine Concept" className="w-full h-full object-cover shadow-[0_0_40px_rgba(0,0,0,0.8)] border border-white/10" data-id="element-7510" />
                <div className="absolute inset-0 border border-[#D4AF37]/30 m-4 pointer-events-none" data-id="element-7511" />
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20" data-id="element-7512">
                  <span className="bg-black/80 text-[#D4AF37] text-[10px] tracking-widest uppercase px-4 py-2 backdrop-blur-sm border border-[#D4AF37]/30" data-id="element-7513">
                    Click to Enlarge
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 24. Cartier Collaboration - Billboard */}
        <section className="w-screen h-screen flex-shrink-0 overflow-hidden relative flex items-center justify-center bg-black" data-id="element-7514">
          <PunjabiPlacard word="ਪ੍ਰਭਾਵ" translation="Impact" data-id="element-7515" />

          {/* Full-bleed billboard image */}
          <motion.div className="absolute inset-0 cursor-pointer group" initial={{
          opacity: 0,
          scale: 1.04
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.4,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} onClick={() => setSelectedImage('https://cdn.magicpatterns.com/uploads/6CMkBmPZYB8UPFpRm8EZSo/Gemini_Generated_Image_z4wlxz4wlxz4wlxz.png')} data-id="element-7516">
            <img src="https://cdn.magicpatterns.com/uploads/6CMkBmPZYB8UPFpRm8EZSo/Gemini_Generated_Image_z4wlxz4wlxz4wlxz.png" alt="Cartier Billboard Concept" className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-[1200ms]" data-id="element-7517" />
            {/* Readability scrims top & bottom */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black/70 pointer-events-none" data-id="element-7518" />

            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20" data-id="element-7519">
              <span className="bg-black/80 text-[#D4AF37] text-[10px] tracking-widest uppercase px-4 py-2 backdrop-blur-sm border border-[#D4AF37]/30" data-id="element-7520">
                Click to Enlarge
              </span>
            </div>
          </motion.div>

          {/* Overlaid heading */}
          <motion.div className="relative z-10 text-center px-6 mt-[-40vh] pointer-events-none" initial={{
          opacity: 0,
          y: -20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.3
        }} viewport={{
          once: true
        }} data-id="element-7521">
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4 drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]" data-id="element-7522">
              The Vision
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] max-w-3xl mx-auto drop-shadow-[0_4px_16px_rgba(0,0,0,0.95)]" data-id="element-7523">
              Bringing the legacy to the streets of the world.
            </h2>
          </motion.div>
        </section>

        {/* 25. The Lahore Darbar Leadership Lab — MBA capstone */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" data-id="element-7524">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7525" />
          <PunjabiPlacard word="ਅਗਵਾਈ" translation="Leadership" data-id="element-7526" />

          <div className="relative z-10 max-w-4xl mx-auto px-6 w-full flex flex-col items-center text-center" data-id="element-7527">
            <motion.div initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-7528">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-7529">
                MBA Capstone · Ivey Business School
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-tight" data-id="element-7530">
                The Lahore Darbar{' '}
                <span className="text-[#D4AF37]" data-id="element-7531">Leadership Lab</span>
              </h2>
            </motion.div>

            {/* The question — centerpiece */}
            <motion.p className="font-serif italic text-2xl sm:text-3xl md:text-4xl text-[#FDFBF7] leading-snug max-w-3xl mx-auto my-10" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.2,
            delay: 0.2
          }} viewport={{
            once: true
          }} data-id="element-7532">
              “How do you build institutions strong enough to{' '}
              <span className="text-[#D4AF37]" data-id="element-7533">
                outlive a brilliant founder?
              </span>
              ”
            </motion.p>

            {/* 3-step flow, minimal */}
            <motion.div className="flex items-center justify-center gap-4 sm:gap-8 mb-10 text-sm" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.35
          }} viewport={{
            once: true
          }} data-id="element-7534">
              {['Prepare', 'Step Inside', 'Advise the Darbar'].map((label, i) => <Fragment key={label} data-id="element-7535">
                    {i > 0 && <span className="text-[#D4AF37]/50 text-lg" data-id="element-7536">→</span>}
                    <span className="text-[#FDFBF7] tracking-wide font-light" data-id="element-7537">
                      {label}
                    </span>
                  </Fragment>)}
            </motion.div>

            {/* Ivey image band */}
            <motion.div className="grid grid-cols-2 gap-4 w-full max-w-2xl" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.45
          }} viewport={{
            once: true
          }} data-id="element-7538">
              {['https://cdn.magicpatterns.com/uploads/uPbfJuRcUJBTsH767Ydzun/richard-ivey-building-southwest-corner.jpg', 'https://cdn.magicpatterns.com/uploads/eei1VL3A4XPSuCegXUhYvS/145-ellisdon-ivey-school-of-business-edit.jpg'].map(src => <div key={src} className="relative overflow-hidden cursor-pointer group border border-white/10" onClick={() => setSelectedImage(src)} data-id="element-7539">
                  <img src={src} alt="Ivey Business School" className="w-full h-32 sm:h-40 object-cover group-hover:scale-[1.05] transition-transform duration-700" data-id="element-7540" />
                </div>)}
            </motion.div>
            <p className="text-white/40 text-[0.65rem] tracking-[0.25em] uppercase mt-4" data-id="element-7541">
              The case method, brought to life
            </p>
          </div>
        </section>
      </motion.div>

      {/* Lightbox Modal */}
      <AnimatePresence data-id="element-7542">
        {selectedImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 sm:p-8 cursor-pointer" onClick={() => setSelectedImage(null)} data-id="element-7543">
            <motion.img initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain rounded-sm shadow-[0_0_50px_rgba(212,175,55,0.15)]" data-id="element-7544" />
            <div className="absolute top-6 right-6 text-white/50 text-sm font-light tracking-widest uppercase" data-id="element-7545">
              Close
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};